import { endpoints } from '@/api';
import { axiosInstance } from '@/lib/utils';
import type { RequestResponse } from '@/types';
import type {
  ReferralEarningsEntry,
  ReferralSubscriptionCreditPayload,
  ReferralSubscriptionCreditResult,
  ReferralWithdrawalPayload,
  ReferralWithdrawalResult,
} from '@/types/referral';
import {
  referralMockState,
  waitForReferralMock,
} from '@/api/queries/referrals/mock-state';

const useReferralMockForUndocumentedEndpoints =
  import.meta.env.VITE_USE_REFERRAL_MOCK === 'true';

export const withdrawReferralEarnings = async (
  payload: ReferralWithdrawalPayload
) => {
  const response = await axiosInstance.post<
    RequestResponse<ReferralWithdrawalResult>
  >(endpoints.referrals.withdraw, payload);
  return response.data.data;
};

export const applyReferralSubscriptionCredit = async (
  payload: ReferralSubscriptionCreditPayload
) => {
  if (useReferralMockForUndocumentedEndpoints) {
    await waitForReferralMock(500);

    const appliedAmount = Math.min(
      payload.amount,
      referralMockState.summary.availableBalance
    );
    const nextBalance = Math.max(
      0,
      referralMockState.summary.availableBalance - appliedAmount
    );
    const remainderDue = Math.max(0, payload.amount - appliedAmount);

    referralMockState.summary.availableBalance = nextBalance;
    referralMockState.summary.canWithdraw = nextBalance >= 5000;
    referralMockState.earnings.unshift({
      id: `earn-credit-${Date.now()}`,
      createdAt: new Date().toISOString(),
      description: `₦${appliedAmount.toLocaleString('en-NG')} applied to your subscription`,
      amount: -appliedAmount,
      balanceAfter: nextBalance,
      type: 'SUBSCRIPTION_CREDIT',
    } satisfies ReferralEarningsEntry);

    return {
      appliedAmount,
      remainderDue,
      availableBalance: nextBalance,
    } satisfies ReferralSubscriptionCreditResult;
  }

  const response = await axiosInstance.post<
    RequestResponse<ReferralSubscriptionCreditResult>
  >(endpoints.referrals.applySubscriptionCredit, payload);
  return response.data.data;
};
